import { type ScriptableTooltipContext, type TooltipModel } from "chart.js";
import moment from "moment";

import { type DateInterval } from "@north-beam/nb-common";

interface BodyLine {
  datasetIndex: number;
  label: string;
  landingPage: string;
  adName: string;
  dateRange: DateInterval;
  value: string;
}

export function chartTooltip(context: ScriptableTooltipContext<"line">) {
  const chart = context.chart;
  const tooltip = context.tooltip;
  let tooltipEl = document.getElementById("chartjs-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    document.body.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.flatMap((e) => e.lines) as unknown as BodyLine[];
    const content = tooltipContent(tooltip, lines);

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          class="
            bg-white border border-gray-1 py-3 px-4 rounded-md space-y-2
            shadow whitespace-nowrap ${contentPosition}
          "
        >
          ${content}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-white absolute bottom-[-6px] border-t border-l border-gray-1
            ${arrowPosition} rotate-[225deg]
          "
        ></div>
      </div>
    `;
  }

  const position = chart.canvas.getBoundingClientRect();
  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    top: `${position.top + window.scrollY + tooltip.caretY}px`,
    left: `${position.left + window.scrollX + tooltip.caretX}px`,
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();
    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");
      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");
      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

function tooltipContent(tooltip: TooltipModel<"line">, lines: BodyLine[]) {
  const { startDate, endDate } = lines[0].dateRange;
  let timePeriod = moment(startDate).format("MMM DD, yyyy");
  if (startDate !== endDate) {
    timePeriod += ` - ${moment(endDate).format("MMM DD, yyyy")}`;
  }

  const content = lines
    .map((line, index) => {
      const labelStyle = tooltip.labelColors[index];
      let subtitle = "";

      if (line.landingPage || line.adName) {
        const item1 = line.landingPage
          ? `<span class="text-cosmic">${line.landingPage}</span>`
          : null;
        const item2 = `<span>${line.adName}</span>`;

        subtitle = `
          <div class="text-xxs ml-[22px] mb-1">
            <div class="flex gap-2">${[item1, item2]
              .filter((e) => e)
              /* eslint-disable-next-line quotes */
              .join('<span class="font-bold">/</span>')}</div>
          </div>
        `;
      }

      return `
        <div class="py-1">
          <div class="flex items-center gap-4">
            <div class="text-sm flex-1 flex items-center gap-16 mb-1">
              <div class="flex items-center gap-1.5">
                <div
                  class="rounded-sm w-4 h-[3px]"
                  style="background-color: ${labelStyle.backgroundColor}"
                ></div>
                <div class="truncate font-bold">${line.label}</div>
              </div>
              <div class="flex-1 text-right font-mono">${line.value}</div>
            </div>
          </div>
          ${subtitle}
        </div>
      `;
    })
    .join("");

  return `
    <div>
      <div class="text-sm flex items-center gap-2 text-muted mb-3 ml-0.5">
        <i class="fa-regular fa-clock"></i>
        ${timePeriod}
      </div>
      ${content}
    </div>
  `;
}
